import { useState } from "react";
import useSnackbar from "./useSnackbar";
import { useAppSelector } from "@/store";
import { useLazyGetUploadSignedUrlQuery } from "@/services/signed-url/signed-url-api";
import { useProcessFileForProductCertMutation } from "@/services/product-certificate/certificate-api";
import { roles } from "@/roles";

export const useExcelUpload = () => {
  const [isUploading, setIsUploading] = useState(false);
  const { show } = useSnackbar();
  const { selectedClient } = useAppSelector((state) => state.client);
  const { selectedTenant } = useAppSelector((state) => state.tenantManagement);
  const { user } = useAppSelector((state) => state.auth);

  const [getUploadSignedUrl] = useLazyGetUploadSignedUrlQuery();
  const [processFileForProductCert] = useProcessFileForProductCertMutation();

  // Get the appropriate client ID based on user role
  const getClientId = () => {
    const isTDAdmin = user?.userRole === roles.TDADMIN;
    
    if (isTDAdmin && selectedTenant?.tenantType === "TRADEDIVE_CLIENT") {
      return selectedTenant?._id;
    }
    
    if (selectedClient?._id) {
      return selectedClient._id;
    }
    
    // Fallback for client/partner roles: use their own tenantId
    if (user?.userRole === roles.TDCLIENT || user?.userRole === roles.SPCLIENT) {
      return user?.tenantId;
    }
    
    return undefined;
  };

  const uploadAndProcessExcel = async (file: File, formType = "USMCA", options?: { clientId?: string }) => {
    setIsUploading(true);
    
    try {
      // Determine client ID
      const clientId = options?.clientId || getClientId();
      
      if (!clientId) {
        throw new Error("No client selected. Please select a client to upload certificates.");
      }

      // Step 1: Get signed URL for S3 upload
      const contentType = file.type;
      const fileName = file.name;
      
      const signedUrlResponse = await getUploadSignedUrl({
        fileName,
        contentType,
      }).unwrap();

      const { signedUrl, key } = signedUrlResponse;

      // Step 2: Upload file to S3
      const uploadResponse = await fetch(signedUrl, {
        method: "PUT",
        headers: {
          "Content-Type": contentType,
        },
        body: file,
      });

      if (!uploadResponse.ok) {
        throw new Error(`S3 upload failed: ${uploadResponse.statusText}`);
      }

      // Step 3: Process file with backend using the S3 key, clientId, and formType
      const processResponse = await processFileForProductCert({
        fileUrl: key,
        fileName,
        clientId,
        tenantId: user?.userRole === roles.TDADMIN ? selectedTenant?._id : undefined,
        formType,
      }).unwrap();

      if (processResponse.success) {
        show(
          "Success",
          "Excel file processed and saved successfully!",
          "success"
        );
      } else {
        show(
          "Processing failed",
          processResponse.message || "Failed to process Excel file",
          "error"
        );
      }

      return processResponse;
    } catch (error) {
      console.error("Error uploading and processing Excel file:", error);
      
      const errorMessage = error instanceof Error 
        ? error.message 
        : "An error occurred while processing the file. Please try again.";
      
      show(
        "Upload failed",
        errorMessage,
        "error"
      );
      
      throw error;
    } finally {
      setIsUploading(false);
    }
  };

  const canUpload = () => {
    const clientId = getClientId();
    return !!clientId;
  };

  return {
    uploadAndProcessExcel,
    isUploading,
    canUpload,
    clientId: getClientId(),
  };
};